import React, { useState } from "react";
import { X, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";

/** Demo request modal — preserves existing /api/public/demo-request flow. */
export default function DemoModal({ open, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  if (!open) return null;

  async function submit(e) {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Please share your name and email");
      return;
    }
    setBusy(true);
    try {
      await api.post("/public/demo-request", {
        name, email, phone, company, message,
      });
      setSent(true);
      toast.success("Got it — we'll reach out within 24 hours.");
    } catch (e) {
      toast.error(errMsg(e, "Couldn't submit. Please email info@ecomcatalog.ai"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-md grid place-items-center p-4"
      onClick={onClose}
      data-testid="demo-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg mkt-glass-strong overflow-hidden"
      >
        <div className="relative p-7 border-b border-white/[0.08]">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/[0.08] text-slate-400"
            data-testid="demo-close"
          >
            <X size={16} />
          </button>
          {sent ? (
            <>
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 grid place-items-center mb-4">
                <CheckCircle2 size={22} className="text-white" />
              </div>
              <h3 className="font-mkt-display text-2xl font-semibold">Thanks!</h3>
              <p className="text-sm text-slate-400 mt-2">
                We've received your request. Someone from EcomCatalog AI will reach
                out within 24 hours on <b className="text-white">{email}</b>.
              </p>
            </>
          ) : (
            <>
              <div className="text-[10px] uppercase tracking-[0.25em] text-indigo-400 font-bold">
                Book a demo
              </div>
              <h3 className="font-mkt-display text-2xl font-semibold mt-1">
                See EcomCatalog AI live.
              </h3>
              <p className="text-sm text-slate-400 mt-1.5">
                15-min walkthrough — AI Listings, Photoshoots & Analytics.
              </p>
            </>
          )}
        </div>

        {!sent && (
          <form onSubmit={submit} className="p-7 space-y-3" data-testid="demo-form">
            <div className="grid grid-cols-2 gap-3">
              <input
                value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Your name *" data-testid="demo-name"
                className="px-3.5 py-2.5 bg-white/[0.03] border border-white/[0.1] rounded-xl text-white text-sm focus:outline-none focus:border-indigo-400/60 transition"
              />
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="Email *" data-testid="demo-email"
                className="px-3.5 py-2.5 bg-white/[0.03] border border-white/[0.1] rounded-xl text-white text-sm focus:outline-none focus:border-indigo-400/60 transition"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input
                value={phone} onChange={(e) => setPhone(e.target.value)}
                placeholder="Phone (optional)" data-testid="demo-phone"
                className="px-3.5 py-2.5 bg-white/[0.03] border border-white/[0.1] rounded-xl text-white text-sm focus:outline-none focus:border-indigo-400/60 transition"
              />
              <input
                value={company} onChange={(e) => setCompany(e.target.value)}
                placeholder="Brand / company" data-testid="demo-company"
                className="px-3.5 py-2.5 bg-white/[0.03] border border-white/[0.1] rounded-xl text-white text-sm focus:outline-none focus:border-indigo-400/60 transition"
              />
            </div>
            <textarea
              rows={3} value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder="What would you like to see? (optional)"
              data-testid="demo-message"
              className="w-full px-3.5 py-2.5 bg-white/[0.03] border border-white/[0.1] rounded-xl text-white text-sm focus:outline-none focus:border-indigo-400/60 transition"
            />
            <button
              type="submit" disabled={busy}
              className="mkt-btn-gradient w-full justify-center text-sm disabled:opacity-60"
              data-testid="demo-submit"
            >
              {busy ? "Sending…" : (<><Send size={14} /> Book my demo</>)}
            </button>
            <div className="text-[11px] text-slate-500 text-center pt-1">
              Or email{" "}
              <a className="text-indigo-300 hover:text-indigo-200 underline-offset-2 hover:underline" href="mailto:info@ecomcatalog.ai">
                info@ecomcatalog.ai
              </a>{" "}
              directly.
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
